import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, Loader2, FileSpreadsheet } from "lucide-react";
import { apiGet } from "@/lib/api";
import { SearchableSelect } from "@/components/ui/searchable-select";

export const Route = createFileRoute("/_authenticated/admin/export")({
  component: ExportPage,
});

interface ProjectOption {
  id: string;
  name: string;
}

function ExportPage() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [projectId, setProjectId] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [downloading, setDownloading] = useState(false);

  // Fetch projects for filter
  const { data: projectsData } = useQuery({
    queryKey: ["projects", "active"],
    queryFn: () =>
      apiGet<{ data: ProjectOption[] }>("/api/projects?status=active"),
  });

  const projects = projectsData?.data ?? [];

  async function handleExport() {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      if (projectId && projectId !== "all") params.set("projectId", projectId);
      if (status && status !== "all") params.set("status", status);

      const BASE_URL = import.meta.env.VITE_API_URL || "";
      const response = await fetch(
        `${BASE_URL}/api/admin/export/flha?${params.toString()}`,
        {
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 401) {
        toast.error("Session expired. Please log in again.");
        window.location.href = "/login";
        return;
      }

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(
          (body as { error?: string }).error || "Export failed"
        );
      }

      // Download the CSV
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `flha-export-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success("Export downloaded");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to export data"
      );
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-text-primary">Export</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Export FLHA records as CSV for analysis or COR audits.</p>
      </div>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-paris-red-text" />
            Export FLHA Data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Export FLHA records as a CSV file for analysis or COR audit
            reporting. Filter by date range, project, and status.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="start-date" className="text-sm">
                Start Date
              </Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-9"
              />
            </div>
            <div>
              <Label htmlFor="end-date" className="text-sm">
                End Date
              </Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-9"
              />
            </div>
            <div>
              <Label htmlFor="export-project" className="text-sm">
                Project
              </Label>
              <SearchableSelect
                className="h-9"
                value={projectId}
                onChange={setProjectId}
                allLabel="All Projects"
                placeholder="All Projects"
                options={projects.map((p) => ({ value: p.id, label: p.name }))}
              />
            </div>
            <div>
              <Label htmlFor="export-status" className="text-sm">
                Status
              </Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={handleExport}
              disabled={downloading}
              className="bg-paris-red hover:bg-paris-red/90"
            >
              {downloading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Download CSV
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="py-4">
          <h3 className="font-medium text-sm mb-2">CSV Contents</h3>
          <p className="text-xs text-muted-foreground">
            The export includes: FLHA ID, Status, Project, Creator, Work Area,
            Shift, Task Count, Hazard Count, Participant Count, High Risk flag,
            Worker Notes, Created/Submitted/Reviewed dates.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
